/**
 * Wave A — Organizations API client (Phase 1 implementation).
 *
 * Replaces the Phase 0 throwing skeleton with a real `fetch`-based client.
 * Used only when `NEXT_PUBLIC_RBAC_MODE !== 'legacy'`. In `legacy` mode the
 * hooks short-circuit and never instantiate the client.
 *
 * Implementation notes:
 *   - Base URL: `import.meta.env.VITE_ORGANIZATIONS_API_URL`
 *               (or `process.env.NEXT_PUBLIC_ORGANIZATIONS_API_URL` if running
 *               under a Next.js host); falls back to `/api/organizations`
 *               which the BFF proxies to the upstream service.
 *   - Auth: bearer token returned by the supplied `getToken()` callback.
 *   - Active tenant: when set, every request carries `X-Active-Tenant`.
 *   - Cache: `getMyPermissions()` is memoized for 60s in-process to avoid
 *     hammering the PDP from concurrent React subtrees that mount before the
 *     TanStack Query cache primes.
 *
 * @module api/wave-a/client
 */

import type {
  Grant,
  GrantsCheckRequest,
  GrantsCheckResponse,
  Invitation,
  Membership,
  MyPermissionsResponse,
  Permission,
  ResourceType,
  Role,
  ScopeType,
  SeatType,
  SubjectType,
} from './types';

/**
 * Token provider contract. The hosting app supplies a function that returns
 * a fresh access token. The client never caches tokens itself.
 */
export type GetTokenFn = () => string | Promise<string | null> | null;

/** Optional callback that returns the currently active tenant id, if any. */
export type GetActiveTenantFn = () => string | null;

/** Filters supported by `GET /grants`. */
export interface ListGrantsFilters {
  subject_id?: string;
  resource_id?: string;
  role_id?: string;
  /** When true, returns revoked and expired grants too. Default: false. */
  including_expired?: boolean;
}

/** Cursor-paginated grants response. */
export interface ListGrantsResponse {
  grants: Grant[];
  cursor?: string;
}

/**
 * Resolves the Organizations API base URL. Order:
 *   1. `import.meta.env.VITE_ORGANIZATIONS_API_URL` (Vite host)
 *   2. `process.env.NEXT_PUBLIC_ORGANIZATIONS_API_URL` (Next.js host)
 *   3. `/api/organizations` (BFF proxy fallback)
 *
 * The Next.js fallback is kept so the same client can be reused if the BFF
 * adds an SSR/edge consumer.
 */
export function resolveBaseUrl(): string {
  // Vite (current host).
  try {
    const viteEnv =
      ((import.meta as unknown as { env?: Record<string, string | undefined> }).env) || {};
    const fromVite = viteEnv.VITE_ORGANIZATIONS_API_URL as string | undefined;
    if (fromVite) return fromVite.replace(/\/$/, '');
  } catch {
    // ignore — not under Vite at runtime
  }
  // Next.js (future host) — keep the originally specified env name.
  if (typeof process !== 'undefined' && process.env) {
    const fromNext = process.env.NEXT_PUBLIC_ORGANIZATIONS_API_URL;
    if (fromNext) return fromNext.replace(/\/$/, '');
  }
  return '/api/organizations';
}

/**
 * Wave A Organizations API client.
 *
 * Constructed with a token provider (and optionally an active-tenant getter
 * + a custom base URL). All write operations are idempotent on
 * `(subject_id, resource_id, role_id)` triples — duplicate creates return
 * the existing grant.
 */
export class OrganizationsApiClient {
  private readonly baseUrl: string;
  private readonly getToken: GetTokenFn;
  private readonly getActiveTenant: GetActiveTenantFn | null;

  /** In-process cache for `/me/permissions` (60s TTL). */
  private mePermsCache: { value: MyPermissionsResponse; expiresAt: number } | null = null;
  private static readonly ME_PERMS_TTL_MS = 60_000;

  constructor(
    getToken: GetTokenFn,
    options?: {
      baseUrl?: string;
      getActiveTenant?: GetActiveTenantFn;
    },
  ) {
    this.baseUrl = (options?.baseUrl ?? resolveBaseUrl()).replace(/\/$/, '');
    this.getToken = getToken;
    this.getActiveTenant = options?.getActiveTenant ?? null;
  }

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------

  private async buildHeaders(extra?: HeadersInit): Promise<Headers> {
    const headers = new Headers(extra);
    headers.set('Accept', 'application/json');
    if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');

    const tokenMaybe = this.getToken();
    const token =
      tokenMaybe && typeof (tokenMaybe as Promise<string>).then === 'function'
        ? await (tokenMaybe as Promise<string | null>)
        : (tokenMaybe as string | null);
    if (token) headers.set('Authorization', `Bearer ${token}`);

    const tenant = this.getActiveTenant?.() ?? null;
    if (tenant) headers.set('X-Active-Tenant', tenant);

    return headers;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    init?: RequestInit,
  ): Promise<T> {
    const url = `${this.baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
    const headers = await this.buildHeaders(init?.headers);
    const res = await fetch(url, {
      ...init,
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: init?.credentials ?? 'include',
    });
    if (res.status === 204) return undefined as unknown as T;
    const text = await res.text();
    const data = text ? safeParseJson(text) : null;
    if (!res.ok) {
      const err = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
      const error = new Error(
        (typeof err.message === 'string' && err.message) || `Wave A API ${res.status} on ${path}`,
      ) as Error & { status?: number; error_code?: string; details?: unknown };
      error.status = res.status;
      error.error_code = typeof err.error_code === 'string' ? (err.error_code as string) : undefined;
      error.details = err.details;
      throw error;
    }
    return data as T;
  }

  private query(params: Record<string, string | number | boolean | undefined>): string {
    const search = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === '') continue;
      search.set(k, String(v));
    }
    const s = search.toString();
    return s ? `?${s}` : '';
  }

  // -------------------------------------------------------------------------
  // Permissions / PDP
  // -------------------------------------------------------------------------

  /** Clears the in-process cache for `/me/permissions`. */
  clearMyPermissionsCache(): void {
    this.mePermsCache = null;
  }

  /**
   * Returns the calling user's effective permissions across all resources.
   * Cached client-side via TanStack Query; this method also keeps a 60s
   * in-process LRU-of-1 to coalesce burst calls.
   */
  async getMyPermissions(options?: { force?: boolean }): Promise<MyPermissionsResponse> {
    const now = Date.now();
    if (!options?.force && this.mePermsCache && this.mePermsCache.expiresAt > now) {
      return this.mePermsCache.value;
    }
    const value = await this.request<MyPermissionsResponse>('GET', '/me/permissions');
    this.mePermsCache = {
      value,
      expiresAt: Date.now() + OrganizationsApiClient.ME_PERMS_TTL_MS,
    };
    return value;
  }

  /** Server-side PDP check. */
  async checkGrant(req: GrantsCheckRequest): Promise<GrantsCheckResponse> {
    return this.request<GrantsCheckResponse>('POST', '/grants/check', req);
  }

  // -------------------------------------------------------------------------
  // Grants
  // -------------------------------------------------------------------------

  async listGrants(filters?: ListGrantsFilters): Promise<ListGrantsResponse> {
    return this.request<ListGrantsResponse>(
      'GET',
      `/grants${this.query({
        subject_id: filters?.subject_id,
        resource_id: filters?.resource_id,
        role_id: filters?.role_id,
        including_expired: filters?.including_expired,
      })}`,
    );
  }

  async getGrant(id: string): Promise<Grant> {
    return this.request<Grant>('GET', `/grants/${encodeURIComponent(id)}`);
  }

  async createGrant(input: {
    subject_id: string;
    subject_type: SubjectType;
    resource_id: string;
    resource_type: ResourceType;
    role_id: string;
    expires_at?: string;
  }): Promise<Grant> {
    return this.request<Grant>('POST', '/grants', input);
  }

  async revokeGrant(id: string): Promise<void> {
    await this.request<void>('DELETE', `/grants/${encodeURIComponent(id)}`);
  }

  // -------------------------------------------------------------------------
  // Memberships
  // -------------------------------------------------------------------------

  async listMemberships(filters: { resource_id?: string; user_id?: string }): Promise<Membership[]> {
    return this.request<Membership[]>(
      'GET',
      `/memberships${this.query({
        resource_id: filters.resource_id,
        user_id: filters.user_id,
      })}`,
    );
  }

  async createMembership(input: {
    user_id: string;
    resource_id: string;
    resource_type: 'account' | 'tenant';
    job_title?: string;
    seat_type?: SeatType;
  }): Promise<Membership> {
    return this.request<Membership>('POST', '/memberships', input);
  }

  async updateMembership(
    id: string,
    patch: { job_title?: string; seat_type?: SeatType },
  ): Promise<Membership> {
    return this.request<Membership>('PATCH', `/memberships/${encodeURIComponent(id)}`, patch);
  }

  async removeMembership(id: string): Promise<void> {
    await this.request<void>('DELETE', `/memberships/${encodeURIComponent(id)}`);
  }

  // -------------------------------------------------------------------------
  // Roles
  // -------------------------------------------------------------------------

  async listRoles(filters?: { scope_type?: ScopeType; is_system?: boolean }): Promise<Role[]> {
    return this.request<Role[]>(
      'GET',
      `/roles${this.query({
        scope_type: filters?.scope_type,
        is_system: filters?.is_system,
      })}`,
    );
  }

  async getRole(id: string): Promise<Role> {
    return this.request<Role>('GET', `/roles/${encodeURIComponent(id)}`);
  }

  async createCustomRole(input: {
    scope_type: ScopeType;
    name: string;
    description?: string;
    permissions: string[];
  }): Promise<Role> {
    return this.request<Role>('POST', '/roles', input);
  }

  async updateRole(
    id: string,
    patch: { name?: string; description?: string; permissions?: string[] },
  ): Promise<Role> {
    return this.request<Role>('PATCH', `/roles/${encodeURIComponent(id)}`, patch);
  }

  async deleteRole(id: string): Promise<void> {
    await this.request<void>('DELETE', `/roles/${encodeURIComponent(id)}`);
  }

  // -------------------------------------------------------------------------
  // Permission catalog
  // -------------------------------------------------------------------------

  async listPermissions(): Promise<Permission[]> {
    return this.request<Permission[]>('GET', '/permissions');
  }

  // -------------------------------------------------------------------------
  // Invitations
  // -------------------------------------------------------------------------

  async listInvitations(filters?: {
    resource_id?: string;
    status?: Invitation['status'];
  }): Promise<Invitation[]> {
    return this.request<Invitation[]>(
      'GET',
      `/invitations${this.query({
        resource_id: filters?.resource_id,
        status: filters?.status,
      })}`,
    );
  }

  async createInvitation(input: {
    email: string;
    resource_id: string;
    resource_type: 'account' | 'tenant';
    role_id: string;
    job_title?: string;
    expires_at?: string;
  }): Promise<Invitation> {
    return this.request<Invitation>('POST', '/invitations', input);
  }

  async acceptInvitation(id: string): Promise<{ user_id: string }> {
    return this.request<{ user_id: string }>(
      'POST',
      `/invitations/${encodeURIComponent(id)}/accept`,
    );
  }

  async cancelInvitation(id: string): Promise<void> {
    await this.request<void>('DELETE', `/invitations/${encodeURIComponent(id)}`);
  }
}

function safeParseJson(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

/**
 * Default token provider sourcing the access token from `localStorage`.
 * Matches the existing axios interceptor in `src/shared/utils/api.ts`.
 */
export const defaultGetToken: GetTokenFn = () => {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem('accessToken');
};

/**
 * Default active-tenant provider sourcing from `localStorage` under the key
 * used by `useActiveTenant`.
 */
export const defaultGetActiveTenant: GetActiveTenantFn = () => {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem('janus.activeTenant');
};

/**
 * Singleton client used by hooks. Tests can replace it via
 * `setOrganizationsClient(...)` to inject a fake.
 */
let _client: OrganizationsApiClient | null = null;

export function getOrganizationsClient(): OrganizationsApiClient {
  if (!_client) {
    _client = new OrganizationsApiClient(defaultGetToken, {
      getActiveTenant: defaultGetActiveTenant,
    });
  }
  return _client;
}

export function setOrganizationsClient(client: OrganizationsApiClient | null): void {
  _client = client;
}
